import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../../../../../../../services/Api/Admin/adminApi";
import {
	CrashReportApi,
	CrashReportOccurrenceApi,
} from "../../../../../../../../../../../../../../services/Api/Admin/adminTypes";
import { mapResponseToResponseDataWithId } from "../../../../../../../../../../services/mapResponseToResponseDataWithId";
import SubmenuCrashReportNavigation from "../../../../components/SubmenuCrashReportNavigation";

interface ListState {
	resource?: Record<string, any>;
}

interface MatchParams {
	crashReportId: string;
}

const listName = "single_crash_report_occurrences";
const resourceType = "single_crash_report_occurrences";

const List = ({ resource }: ListState) => {
	const { t } = useTranslation("admin");
	const location = useLocation();
	const segmentContextValue = React.useContext(SegmentContext);

	const { crashReportId } = useParams<MatchParams>();

	if (!resource) return null;
	const data = resource.read();

	let config = {
		fields: [
			{
				id: "app_name",
				name: t("modules.crash_report.field.app_name.title"),
				render: (data: CrashReportOccurrenceApi) => (
					<Link to={`/admin/crash_reports/edit/${data.crash_report_id}/occurrences/${data.id}`}>
						{data.app_name}
					</Link>
				),
				renderExport: (data: CrashReportOccurrenceApi) => data.app_name,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsAppNamesSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "app_version",
				name: t("common.word.app_version"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsAppVersionsSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "created_at",
				name: t("modules.crash_report.field.created_at.title"),
				type: "date",
				render: (data: CrashReportOccurrenceApi) => FormatDate(data.created_at),
			},
			{
				id: "terminal",
				name: t("common.word.terminal"),
				render: (item: CrashReportOccurrenceApi) => item.terminal && `${item.terminal}`,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsTerminalsSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "organization_name",
				name: t("lib:common.word.venue"),
				render: (item: CrashReportApi) => {
					const id = item.organization_name?.substring(
						item.organization_name.indexOf("(") + 1,
						item.organization_name.indexOf(")")
					);
					const name = id ? item.organization_name.split(`(${id}`)[0] : item.organization_name;
					return (
						<div className="d-flex flex-column">
							{name}
							{id && <small className="text-muted">{id.split("=").join(" ")}</small>}
						</div>
					);
				},
				renderExport: (item: CrashReportApi) => item.organization_name && `${item.organization_name}`,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsOrganizationNamesSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
		],
		selectedColumns: ["app_name", "app_version", "created_at", "terminal", "organization_name"],
		externalSelectedFilters: [
			{
				id: "crash_report",
				filterType: "number",
				value: crashReportId,
				condition: "e",
				visible: false,
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: CrashReportApi) => `${location.pathname}/${item.id}`,
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return adminApi.getCrashReportsOccurrences(params, { cancelToken: sourceToken?.token });
		},
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: `${t("modules.crash_report.field.occurrences.header.title")} #${crashReportId}`,
			filename: `${t("modules.crash_report.field.occurrences.field.export_config.filename")} ${crashReportId}`,
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;

	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return <ListData config={config} />;
};

const CrashReportEditIndexOccurrencesPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const { crashReportId } = useParams<MatchParams>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(
			`#${crashReportId} | ${t("modules.crash_report.field.occurrences.header.title")} | ${t(
				"lib:go_component.navigation.admin"
			)}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && (
				<Header title={`${t("modules.crash_report.field.occurrences.header.title")} #${crashReportId}`} />
			)}
			<SubmenuCrashReportNavigation crashReportId={crashReportId} />
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default CrashReportEditIndexOccurrencesPage;
